import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useWS } from '../../contexts/WSContext';
import api from '../../services/api';
import toast from 'react-hot-toast';

// Leaflet map for tracking
function TrackingMap({ customerLoc, deliveryLoc }) {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const markersRef = useRef({});

  useEffect(() => {
    if (!mapRef.current || mapObj.current) return;
    const L = window.L;
    if (!L) return;

    // Fix default icon
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
      iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    });

    const map = L.map(mapRef.current, { zoomControl: false }).setView(
      [customerLoc.lat, customerLoc.lng], 14,
    );
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
    }).addTo(map);
    L.control.zoom({ position: 'bottomright' }).addTo(map);

    const custIcon = L.divIcon({
      html: '<div style="font-size:24px;line-height:1">🏠</div>',
      className: '', iconAnchor: [12, 24],
    });
    const delIcon = L.divIcon({
      html: '<div style="font-size:24px;line-height:1">🛵</div>',
      className: '', iconAnchor: [12, 24],
    });

    markersRef.current.customer = L.marker([customerLoc.lat, customerLoc.lng], { icon: custIcon })
      .bindPopup('Sua localização').addTo(map);

    if (deliveryLoc) {
      markersRef.current.delivery = L.marker([deliveryLoc.lat, deliveryLoc.lng], { icon: delIcon })
        .bindPopup('Entregador').addTo(map);
    }

    mapObj.current = map;
  }, []);

  useEffect(() => {
    if (!mapObj.current || !deliveryLoc) return;
    const L = window.L;
    const delIcon = L.divIcon({
      html: '<div style="font-size:24px;line-height:1">🛵</div>',
      className: '', iconAnchor: [12, 24],
    });
    if (markersRef.current.delivery) {
      markersRef.current.delivery.setLatLng([deliveryLoc.lat, deliveryLoc.lng]);
    } else {
      markersRef.current.delivery = L.marker([deliveryLoc.lat, deliveryLoc.lng], { icon: delIcon })
        .bindPopup('Entregador').addTo(mapObj.current);
    }
    // Draw route line
    if (markersRef.current.routeLine) markersRef.current.routeLine.remove();
    markersRef.current.routeLine = L.polyline(
      [[customerLoc.lat, customerLoc.lng], [deliveryLoc.lat, deliveryLoc.lng]],
      { color: '#FF6B00', weight: 3, dashArray: '8,8' },
    ).addTo(mapObj.current);

    mapObj.current.fitBounds(
      [[customerLoc.lat, customerLoc.lng], [deliveryLoc.lat, deliveryLoc.lng]],
      { padding: [40, 40] },
    );
  }, [deliveryLoc]);

  return <div ref={mapRef} className="w-full h-full" />;
}

export default function CustomerTracking() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const { on, connect } = useWS();
  const [order, setOrder] = useState(null);
  const [deliveryLoc, setDeliveryLoc] = useState(null);
  const [leafletReady, setLeafletReady] = useState(false);

  useEffect(() => {
    // Load leaflet script if not loaded
    if (window.L) { setLeafletReady(true); return; }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => setLeafletReady(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) connect(token);
  }, []);

  useEffect(() => {
    api.get(`/orders/${orderId}`).then(r => {
      setOrder(r.data);
      if (r.data.deliveryLocation) setDeliveryLoc(r.data.deliveryLocation);
    });
  }, [orderId]);

  useEffect(() => {
    const off1 = on('location_update', (msg) => {
      if (msg.orderId === orderId) setDeliveryLoc(msg.location);
    });
    const off2 = on('order_status', (msg) => {
      if (msg.orderId === orderId && msg.status === 'delivered') {
        toast.success('Entrega concluída! 🎉');
        nav('/cliente/historico');
      }
    });
    return () => { off1(); off2(); };
  }, [on, orderId]);

  // Poll for location updates every 8s
  useEffect(() => {
    const t = setInterval(() => {
      api.get(`/orders/${orderId}`).then(r => {
        if (r.data.deliveryLocation) setDeliveryLoc(r.data.deliveryLocation);
        if (r.data.status === 'delivered') {
          toast.success('Entrega concluída! 🎉');
          nav('/cliente/historico');
        }
      });
    }, 8000);
    return () => clearInterval(t);
  }, [orderId]);

  if (!order) return <div className="screen flex items-center justify-center"><div className="text-4xl animate-spin">🔥</div></div>;

  const customerLoc = order.location || { lat: -23.5505, lng: -46.6333 };

  return (
    <div className="screen flex flex-col">
      {/* Map */}
      <div className="flex-1 relative" style={{ minHeight: 0 }}>
        {leafletReady ? (
          <TrackingMap customerLoc={customerLoc} deliveryLoc={deliveryLoc} />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500">
            <span className="animate-spin text-3xl">🗺</span>
          </div>
        )}
        {/* Header overlay */}
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-[#1A1A2E]/90 to-transparent px-4 pt-12 pb-6 text-white z-10">
          <h1 className="font-black text-lg">Rastreando Entrega</h1>
          {order.estimatedMinutes && (
            <p className="text-orange-300 text-sm">Previsão: ~{order.estimatedMinutes} minutos</p>
          )}
        </div>
      </div>

      {/* Bottom card */}
      <div className="bg-white px-4 py-4 shadow-lg rounded-t-3xl slide-up">
        <div className="w-10 h-1 bg-gray-200 rounded mx-auto mb-4" />

        {/* Status */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-3 h-3 rounded-full bg-primary animate-pulse" />
          <span className="font-bold text-gray-800">
            {order.status === 'accepted' ? 'Entregador indo buscar o gás' :
             order.status === 'in_transit' ? 'A caminho de você' :
             'Entregue!'}
          </span>
        </div>

        {/* Delivery person */}
        {order.deliveryPerson && (
          <div className="bg-gray-50 rounded-2xl p-3 flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-2xl">🛵</div>
            <div className="flex-1">
              <div className="font-bold text-gray-800">{order.deliveryPerson.name}</div>
              <div className="text-xs text-gray-500">{order.deliveryPerson.vehicle} • ⭐ {order.deliveryPerson.rating?.toFixed(1)}</div>
            </div>
            <a
              href={`tel:${order.deliveryPerson.phone}`}
              className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xl"
            >📞</a>
          </div>
        )}

        {/* Order info */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="text-xl">{order.brandEmoji}</span>
          <span className="font-semibold">{order.brandName} {order.sizeLabel}</span>
          <span className="ml-auto text-primary font-bold">R$ {order.customerPrice?.toFixed(2)}</span>
        </div>
        <div className="text-xs text-gray-400 mt-1">📍 {order.address}</div>
      </div>
    </div>
  );
}
